import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import {
  SpacerService,
  SpacerTestInputDto,
  SpacerQaqcTitleDto,
  SpacerHpHtRheologyInputDto,
  BatchTestsBriefDto,
  SlurryStatuses,
  ProjectStatuses,
  TestTypes,
} from '../../../services/spacer.service';
import { ProjectDetailsService } from '../../../services/project-details.service';
import { CisProSearchComponent } from '../../cispro-search/cispro-search.component';
import { CisProSearchResult } from '../../../services/cispro-search.service';
import { SpacerTestAttachmentsDialogComponent, SpacerTestAttachmentsDialogData } from '../spacer-test-attachments-dialog/spacer-test-attachments-dialog.component';
import { SpacerTestAppendNoteDialogComponent, SpacerTestAppendNoteDialogData } from '../spacer-test-append-note-dialog/spacer-test-append-note-dialog.component';

interface SpacerQAQCReading {
  titleId?: number | null;
  title: string;
  rpm600?: number;
  rpm300?: number;
  rpm200?: number;
  rpm100?: number;
  rpm60?: number;
  rpm30?: number;
  rpm20?: number;
  rpm10?: number;
  rpm6?: number;
  rpm3?: number;
  pvCp?: number;
  tyLbf?: number;
}

interface SpacerQAQC {
  testInputMasterId?: number;
  temperature: number | null;
  readings: SpacerQAQCReading[];
  notes: string;
  isMuted: boolean;
  outputs?: BatchTestsBriefDto[];
  attachmentsCount?: number;
}

interface QaqcReadingTitle {
  titleId: number | null;
  title: string;
}

interface SpacerHPHTRheology {
  testInputMasterId?: number;
  equipmentType: string;
  hpHtRheologyTypeId?: number;
  temp: number | null;
  pressure: number | null;
  totalTime: number | null;
  readings?: SpacerHPHTReading[];
  status: string;
  batchNumber: string;
  reviewedBy: string;
  notes: string;
  isMuted: boolean;
  outputs?: BatchTestsBriefDto[];
  attachmentsCount?: number;
}

interface SpacerCompatibilityRatio {
  type1: string;
  materialPercent: number | null;
  type2: string;
  spacerPercent: number | null;
}

interface SpacerCompatibility {
  testInputMasterId?: number;
  temperature: number | null;
  mudContainer: string;
  slurry: string;
  ufSlurryId?: string;
  ratios: SpacerCompatibilityRatio[];
  notes: string;
  isMuted: boolean;
  outputs?: BatchTestsBriefDto[];
  attachmentsCount?: number;
}

interface SpacerSSST {
  testInputMasterId?: number;
  temperature: number | null;
  mudContainer: string;
  notes: string;
  isMuted: boolean;
  outputs?: BatchTestsBriefDto[];
  attachmentsCount?: number;
}
interface SpacerHPHTReading {
  temperature?: number | null;
  pressure?: number | null;
  time?: number | null;
}

@Component({
  selector: 'app-requested-tests',
  imports: [CommonModule, FormsModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './requested-tests.component.html',
  styleUrls: ['./requested-tests.component.css']
})
export class RequestedTestsComponent implements OnInit, OnChanges {
  @Input() spacerId: number | null = null;
  @Input() projectId: number | null = null;
  @Input() slurryStatus: number | undefined;
  @Input() projectStatus: number | undefined;
  @Input() parentQaqcReadingTitles: SpacerQaqcTitleDto[] | null = null;

  // Loading state
  isLoading = false;
  isLoadingTab = false;

  // Spacer Compatibility data
  spacerCompatibilityList: SpacerCompatibility[] = [];
  // Spacer SSST data
  spacerSsstList: SpacerSSST[] = [];
  // Spacer QAQC data
  spacerQAQCList: SpacerQAQC[] = [];
  qaqcReadingTitles: QaqcReadingTitle[] = []; // Store QAQC reading titles with IDs from project configuration
  // Spacer HPHT Rheology data
  spacerHPHTList: SpacerHPHTRheology[] = [];
  slurryOptions: { slurryId: number; slurryName: string }[] = [];
  isLoadingSlurries = false;

  // Equipment types
  equipmentTypes = [
    'Fan77',
    'Chann7600',
    'Chann7550'
  ];
  materialTypes = ['Cement', 'Mud', 'Spacer'];
  spacerRatioType1List = this.materialTypes.map(type => ({ value: type, text: type }));
  spacerRatioType2List = this.materialTypes.map(type => ({ value: type, text: type }));

  StatusTypes = ['In Progress', 'Submitted', 'Approved', 'Rejected', 'Need Retake', 'Completed', 'Cancelled'];

  constructor(
    private spacerService: SpacerService,
    private projectDetailsService: ProjectDetailsService,
    private dialog: MatDialog
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['parentQaqcReadingTitles']) {
      this.setQaqcTitlesFromParent();
      this.ensureQaqcReadingsInitialized();
    }

    if (changes['projectId']) {
      this.slurryOptions = [];
      this.isLoadingSlurries = false;
      this.loadCompatibilitySlurryOptions();
    }
  }

  ngOnInit(): void {
    this.setQaqcTitlesFromParent();

    this.loadCompatibilitySlurryOptions();

    if (this.spacerId) {
      this.loadRequestedTests();
    }
  }
  showCompatibility = false;
  showSSST = false;
  showQAQC = false;
  showHPHT = false;

  public SlurryStatuses = SlurryStatuses;

  loadRequestedTests(): void {
    if (!this.spacerId) return;
    this.isLoading = true;
    this.isLoadingTab = true;
    this.spacerService.getSpacerTestInputs(this.spacerId).subscribe({
      next: (data: SpacerTestInputDto | null) => {
        if (data) {
          this.mapSpacerTestInputs(data);
        } else {
          // No data returned - initialize empty lists
          this.spacerCompatibilityList = [];
          this.spacerSsstList = [];
          this.spacerQAQCList = [];
          this.spacerHPHTList = [];
        }

        this.setQaqcTitlesFromParent();
        this.ensureQaqcReadingsInitialized();

        // If user checked QAQC while initial load was in flight, restore the
        // draft row after server mapping to avoid first-click flicker/hide.
        if (this.showQAQC) {
          this.onQAQCCheck(true);
        }

        if (this.showCompatibility || this.spacerCompatibilityList.length > 0) {
          this.loadCompatibilitySlurryOptions();
        }

        this.isLoading = false;
        this.isLoadingTab = false;
      },
      error: (error) => {
        console.error('Error loading spacer test inputs:', error);
        this.isLoading = false;
        this.isLoadingTab = false;
        // Initialize empty lists on error
        this.spacerCompatibilityList = [];
        this.spacerSsstList = [];
        this.spacerQAQCList = [];
        this.spacerHPHTList = [];
      }
    });
  }

  onCompatibilityCheck() {
    if (this.showCompatibility &&
      this.spacerCompatibilityList.length === 0) {

      this.spacerCompatibilityList.push({
        temperature: null,
        mudContainer: '',
        slurry: '',
        ratios: [],
        notes: '',
        isMuted: false
      });
    }

    if (this.showCompatibility) {
      this.loadCompatibilitySlurryOptions();
    }
  }

  onSSSTCheck(): void {
    if (this.showSSST && this.spacerSsstList.length === 0) {
      this.spacerSsstList.push({
        temperature: null,
        mudContainer: '',
        notes: '',
        isMuted: false,
        outputs: [],
        attachmentsCount: 0
      });
    }
  }

  onQAQCCheck(isChecked: boolean): void {
    this.showQAQC = isChecked;

    if (!isChecked) {
      return;
    }

    this.setQaqcTitlesFromParent();

    if (this.spacerQAQCList.length === 0) {
      this.spacerQAQCList.push({
        temperature: null,
        readings: this.qaqcReadingTitles.map(title => ({
          titleId: title.titleId,
          title: title.title
        })),
        notes: '',
        isMuted: false,
        outputs: [],
        attachmentsCount: 0
      });
      return;
    }

    this.ensureQaqcReadingsInitialized();
  }

  private setQaqcTitlesFromParent(): void {
    const titles = this.parentQaqcReadingTitles ?? [];
    if (titles.length === 0) {
      return;
    }

    this.qaqcReadingTitles = titles.map((t: SpacerQaqcTitleDto) => ({
      titleId: t?.id ?? null,
      title: t?.fullTitle || t?.titleValue?.title || ''
    }));
  }

  private ensureQaqcReadingsInitialized(): void {
    this.spacerQAQCList = this.spacerQAQCList.map(qaqc => {
      if (qaqc.readings && qaqc.readings.length > 0) {
        return qaqc;
      }

      return {
        ...qaqc,
        readings: this.qaqcReadingTitles.map(title => ({
          titleId: title.titleId,
          title: title.title
        }))
      };
    });
  }


  addHphtTest(): void {
    this.spacerHPHTList.push({
      equipmentType: this.equipmentTypes[0],
      temp: null,
      pressure: null,
      totalTime: null,
      readings: [{ temperature: null, pressure: null, time: null }],
      status: '',
      batchNumber: '',
      reviewedBy: '',
      notes: '',
      isMuted: false,
      outputs: [],
      attachmentsCount: 0
    });
  }

  isDraftMode(): boolean {
    return this.slurryStatus === SlurryStatuses.Draft || this.slurryStatus === SlurryStatuses.DraftAgain;
  }

  /** True only for the initial Draft status (not DraftAgain). Used to disable
   *  View Attachments until the test has been saved for the first time. */
  isDraftOnly(): boolean {
    return this.slurryStatus === SlurryStatuses.Draft;
  }

  private readonly defaultCompatibilityRatioLabels: string[] = [
    '100% Mud',
    '95% Mud 5% Spacer',
    '75% Mud 25% Spacer',
    '50% Mud 50% Spacer',
    '25% Mud 75% Spacer',
    '5% Mud 95% Spacer',
    '100% Spacer',
    '100% Cement',
    '95% Cement 5% Spacer',
    '95% Cement 5% Mud',
    '75% Cement 25% Spacer',
    '75% Cement 25% Mud',
    '50% Cement 50% Spacer',
    '50% Cement 50% Mud',
    '25% Cement 75% Spacer',
    '25% Cement 75% Mud',
    '5% Cement 95% Spacer',
    '5% Cement 95% Mud'
  ];

  get defaultCompatibilityRatiosHoverText(): string {
    return this.defaultCompatibilityRatioLabels.join('\n');
  }

  private getDefaultCompatibilityRatios(): SpacerCompatibilityRatio[] {
    return [
      { type1: 'Mud', materialPercent: 95, type2: 'Spacer', spacerPercent: 5 },
      { type1: 'Mud', materialPercent: 75, type2: 'Spacer', spacerPercent: 25 },
      { type1: 'Mud', materialPercent: 50, type2: 'Spacer', spacerPercent: 50 }
    ];
  }

  addCompatibilityRatio(compat: SpacerCompatibility): void {
    compat.ratios.push({
      type1: '',
      materialPercent: null,
      type2: '',
      spacerPercent: null
    });
  }

  removeCompatibilityRatio(compat: SpacerCompatibility, index: number): void {
    compat.ratios.splice(index, 1);
  }

  setDefaultCompatibilityRatios(compat: SpacerCompatibility): void {
    compat.ratios = this.getDefaultCompatibilityRatios();
  }

  onCompatibilityMaterialPercentChange(ratio: SpacerCompatibilityRatio): void {
    if (ratio.materialPercent == null) {
      ratio.spacerPercent = null;
      return;
    }

    ratio.spacerPercent = Math.max(0, 100 - ratio.materialPercent);
  }

  private loadCompatibilitySlurryOptions(): void {

    if (this.isLoadingSlurries || this.slurryOptions.length > 0 || !this.projectId) {
      return;
    }

    this.isLoadingSlurries = true;

    this.projectDetailsService.getProjectById(this.projectId).subscribe({
      next: (project) => {
        const wellId = project?.wellId;
        if (!wellId) {
          this.isLoadingSlurries = false;
          return;
        }

        this.spacerService.getSlurriesForWell(wellId).subscribe({
          next: (slurries) => {
            this.slurryOptions = slurries || [];
            this.reconcileCompatibilitySlurrySelection();
            this.isLoadingSlurries = false;
          },
          error: () => {
            this.isLoadingSlurries = false;
          }
        });
      },
      error: () => {
        this.isLoadingSlurries = false;
      }
    });
  }

  addHphtReading(hpht: SpacerHPHTRheology): void {
    if (!hpht.readings) {
      hpht.readings = [];
    }

    hpht.readings.push({
      temperature: null,
      pressure: null,
      time: null
    });
  }

  removeHphtReading(hpht: SpacerHPHTRheology, index: number): void {
    if (!hpht.readings) {
      return;
    }

    hpht.readings.splice(index, 1);
  }

  searchCispro(field: 'compatibility' | 'ssst', index: number): void {
    const ref = this.dialog.open(CisProSearchComponent, {
      width: '90%',
      maxWidth: '1300px',
      maxHeight: '85vh',
      panelClass: 'cispro-search-dialog'
    });

    ref.afterClosed().subscribe((result: CisProSearchResult | undefined) => {
      if (!result) return;

      if (field === 'compatibility' && this.spacerCompatibilityList[index]) {
        this.spacerCompatibilityList[index].mudContainer = result.materialName;
      }

      if (field === 'ssst' && this.spacerSsstList[index]) {
        this.spacerSsstList[index].mudContainer = result.materialName;
      }
    });
  }

  removeCispro(field: 'compatibility' | 'ssst', index: number): void {
    if (field === 'compatibility' && this.spacerCompatibilityList[index]) {
      this.spacerCompatibilityList[index].mudContainer = '';
    }

    if (field === 'ssst' && this.spacerSsstList[index]) {
      this.spacerSsstList[index].mudContainer = '';
    }
  }
  /**
   * Maps the API response to the UI models
   */
  private mapSpacerTestInputs(data: SpacerTestInputDto): void {
    // Keep QAQC reading titles available even when QAQC test is not created yet.
    const configuredQaqcReadings: SpacerQaqcTitleDto[] = data.sharedSettings?.projectSpacerQaqcReadings || [];
    const mappedQaqcTitles = configuredQaqcReadings.map((r: SpacerQaqcTitleDto) => ({
      titleId: r.id ?? null,
      title: r.fullTitle || r.titleValue?.title || ''
    }));

    // Some responses omit shared settings titles; keep project-loaded titles in that case.
    if (mappedQaqcTitles.length > 0) {
      this.qaqcReadingTitles = mappedQaqcTitles;
    }
    // Map Spacer Compatibility
    if (data.spacerCompatibility) {
      // Map compatibility readings to ratios
      const ratios = this.mapCompatibilityReadingsToRatios(data.spacerCompatibility.readings);

      this.spacerCompatibilityList = [{
        testInputMasterId: data.spacerCompatibility.testInputMasterId,
        temperature: data.spacerCompatibility.temperature || data.sharedSettings?.finalTemperature || null,
        mudContainer: data.spacerCompatibility.mudCisProContainer || '',
        slurry: data.spacerCompatibility.slurryId != null ? String(data.spacerCompatibility.slurryId) : '',
        ufSlurryId: data.spacerCompatibility.ufSlurryId || '',
        ratios: ratios,
        notes: data.spacerCompatibility.notes ?? '',
        isMuted: data.spacerCompatibility.isMuted ?? false,
        outputs: data.spacerCompatibility.outputs || [],
        attachmentsCount: data.spacerCompatibility.attachmentsCount || 0
      }];
      this.showCompatibility = true;
      this.reconcileCompatibilitySlurrySelection();

    } else {
      this.spacerCompatibilityList = [];
    }
    console.log('Loaded Spacer Test Inputs:', {
      spacerCompatibilityList: this.spacerCompatibilityList
    });
    // Map Spacer SSST
    if (data.spacerSsst) {
      this.spacerSsstList = [{
        testInputMasterId: data.spacerSsst.testInputMasterId,
        temperature: data.spacerSsst.temperature || data.sharedSettings?.finalTemperature || 80,
        mudContainer: data.spacerSsst.mudCisProContainer || '',
        notes: data.spacerSsst.notes ?? '',
        isMuted: data.spacerSsst.isMuted ?? false,
        outputs: data.spacerSsst.outputs || [],
        attachmentsCount: data.spacerSsst.attachmentsCount || 0
      }];
      this.showSSST = true;
    } else {
      this.spacerSsstList = [];
    }

    // Map Spacer QAQC
    if (data.spacerQaQc) {
      const readings: SpacerQAQCReading[] = this.qaqcReadingTitles.map(r => ({
        titleId: r.titleId ?? null,
        title: r.title
      }));

      this.spacerQAQCList = [{
        testInputMasterId: data.spacerQaQc.testInputMasterId,
        temperature: data.spacerQaQc.temperature || data.sharedSettings?.finalTemperature || 80,
        readings: readings,
        notes: data.spacerQaQc.notes ?? '',
        isMuted: data.spacerQaQc.isMuted ?? false,
        outputs: data.spacerQaQc.outputs || [],
        attachmentsCount: data.spacerQaQc.attachmentsCount || 0
      }];
      this.showQAQC = true;
    } else {
      this.spacerQAQCList = [];
      this.showQAQC = false;
    }
    // Map Spacer HPHT Rheology
    if (data.spacerHpHtRheologyInputs && data.spacerHpHtRheologyInputs.length > 0) {
      this.spacerHPHTList = data.spacerHpHtRheologyInputs.map(
        (hpht: SpacerHpHtRheologyInputDto) => {

          const firstOutput = hpht.outputs?.[0];
          const firstReading = hpht.readings?.[0];
          const readings: SpacerHPHTReading[] = hpht.readings?.map(reading => ({

            temperature: reading.temperature ?? null,

            pressure: reading.pressure ?? null,

          time: reading.time ?? null})) || [];

          return {
            testInputMasterId: hpht.testInputMasterId,
            equipmentType: this.getEquipmentTypeName(
              hpht.hpHtRheologyTypeId
            ),
            hpHtRheologyTypeId: hpht.hpHtRheologyTypeId,
            temp: firstReading?.temperature ?? null,
            pressure: firstReading?.pressure ?? null,
            totalTime: firstReading?.time ?? null,
            readings: readings,
            status: this.getStatusName(firstOutput?.status),
            batchNumber: firstOutput?.batchNumber ?? '',
            reviewedBy: firstOutput?.reviewedBy ?? '',
            notes: hpht.notes ?? '',
            isMuted: hpht.isMuted ?? false,
            outputs: hpht.outputs ?? [],
            attachmentsCount: hpht.attachmentsCount ?? 0
          };
        }
      );
      this.showHPHT = true;
    } else {
      this.spacerHPHTList = [];
      this.showHPHT = false;
    }
  }

  /**
   * Maps backend compatibility readings to UI ratio format
   */
  private mapCompatibilityReadingsToRatios(readings?: any[]): SpacerCompatibilityRatio[] {
    if (!readings || readings.length === 0) {
      return [];
    }

    return readings.map(reading => {
      // Map ratio type enum to material name
      const type1 = this.mapRatioTypeToName(reading.spacerCompatibilityRatioType1);
      const type2 = this.mapRatioTypeToName(reading.spacerCompatibilityRatioType2);

      // Calculate material percent (100 - spacer percent)
      const spacerPercent = reading.spacerPercent ?? null;
      const materialPercent = spacerPercent != null ? 100 - spacerPercent : null;

      return {
        type1: type1,
        materialPercent: materialPercent,
        type2: type2,
        spacerPercent: spacerPercent
      };
    });
  }

  private reconcileCompatibilitySlurrySelection(): void {
    if (!this.spacerCompatibilityList.length || !this.slurryOptions.length) {
      return;
    }

    this.spacerCompatibilityList = this.spacerCompatibilityList.map(compat => {
      const currentSlurryId = (compat.slurry ?? '').toString().trim();
      if (currentSlurryId && this.slurryOptions.some(s => String(s.slurryId) === currentSlurryId)) {
        return { ...compat, slurry: currentSlurryId };
      }

      const ufSlurryId = (compat.ufSlurryId ?? '').trim().toLowerCase();
      if (!ufSlurryId) {
        return compat;
      }

      const byName = this.slurryOptions.find(s => (s.slurryName ?? '').trim().toLowerCase() === ufSlurryId);
      if (byName) {
        return { ...compat, slurry: String(byName.slurryId) };
      }

      return compat;
    });
  }

  /**
   * Maps compatibility ratio type enum to material name
   * Based on backend enum: 1=Cement, 2=Mud, 3=Spacer
   */
  private mapRatioTypeToName(ratioType?: number): string {
    switch (ratioType) {
      case 1:
        return 'Cement';
      case 2:
        return 'Mud';
      case 3:
        return 'Spacer';
      default:
        return '';
    }
  }

  /**
   * Maps material name to compatibility ratio type enum
   * Reverse of mapRatioTypeToName
   */
  private mapRatioNameToType(ratioName: string): number | undefined {
    switch (ratioName) {
      case 'Cement':
        return 1;
      case 'Mud':
        return 2;
      case 'Spacer':
        return 3;
      default:
        return undefined;
    }
  }

  /**
   * Maps UI ratios array back to backend readings format
   * Reverse of mapCompatibilityReadingsToRatios
   */
  private mapRatiosToCompatibilityReadings(ratios: SpacerCompatibilityRatio[]): any[] {
    return ratios.map(ratio => ({
      spacerPercent: ratio.spacerPercent ?? 0,
      spacerCompatibilityRatioType1: this.mapRatioNameToType(ratio.type1),
      spacerCompatibilityRatioType2: this.mapRatioNameToType(ratio.type2),
      isSlurryBased: false
    }));
  }

  /**
   * Maps hpHtRheologyTypeId to equipment type name
   */

  private getEquipmentTypeName(typeId?: number): string {
    switch (typeId) {
      case 1:
        return 'Fan77';
      case 2:
        return 'Chann7600';
      case 3:
        return 'Chann7550';
      default:
        return '';
    }
  }
  private getStatusName(statusId?: number): string {
    switch (statusId) {
      case 1:
        return 'In Progress';
      case 2:
        return 'Submitted';
      case 3:
        return 'Approved';
      case 4:
        return 'Rejected';
      case 5:
        return 'Need Retake';
      case 6:
        return 'Completed';
      default:
        return '';
    }
  }

  removeSpacerQAQC(index: number): void {
    this.spacerQAQCList.splice(index, 1);
  }

  async recordQAQCOutput(qaqc: SpacerQAQC): Promise<void> {
    if (!qaqc.testInputMasterId || !this.spacerId) {
      console.warn('Missing testInputMasterId or spacerId');
      return;
    }

    const { RecordOutputDialogComponent } = await import('./record-output-dialog/record-output-dialog.component');

    const dialogData = {
      slurryId: this.spacerId,
      testInputMasterId: qaqc.testInputMasterId,
      testType: 'Spacer QAQC',
      testTypeId: TestTypes.SpacerQAQC,
      fallbackTemperature: qaqc.temperature ?? null,
      readingTitles: this.qaqcReadingTitles
    };

    const dialogRef = this.dialog.open(RecordOutputDialogComponent, {
      width: '1400px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: dialogData,
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'record' && result.batch) {

        console.log('Opening QAQC Review Dialog with batch:', result.batch);

        this.reviewQAQC(qaqc, result.batch);
      } else if (result?.statusChanged || result?.updated) {
        // Review form embedded in the record dialog (multiple batches) closed after success
        this.loadRequestedTests();
      }
    });
  }


  async reviewQAQC(qaqc: SpacerQAQC | any, batch?: any): Promise<void> {
    const { ReviewQaqcComponent } = await import('./review-qaqc/review-qaqc.component');

    const batchData = batch || qaqc;
    console.log('batchData before dialog:', batchData);
    console.log('batchData.slurryBatchId:', batchData?.slurryBatchId);
    console.log('qaqc:', qaqc);
    const dialogData = {
      testInputMasterId: batchData.testInputMasterId,
      slurryBatchId:
        batchData.slurryBatchId ??
        qaqc.slurryBatchId,
      slurryId: batchData.slurryId,
      batchNumber: batchData.batchNumber,
      readingTitles: this.qaqcReadingTitles
    };
    console.log('Final Dialog Data:', dialogData);

    const dialogRef = this.dialog.open(ReviewQaqcComponent, {
      width: '1500px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: dialogData,
      disableClose: false
    });

    // Listen for dialog close and refresh data if updated
    dialogRef.afterClosed().subscribe(result => {
      if (result?.updated || result?.statusChanged) {
        console.log('QAQC test was updated, refreshing data...');
        this.loadRequestedTests();
      }
    });
  }

  viewQAQCAttachments(qaqc: SpacerQAQC): void {
    const dialogData: SpacerTestAttachmentsDialogData = {
      testInputMasterId: qaqc.testInputMasterId ?? 0,
      testType: 'Spacer QAQC'
    };

    this.dialog.open(SpacerTestAttachmentsDialogComponent, {
      data: dialogData,
      width: '1200px',
      maxWidth: '95vw'
    });
  }

  appendClearQAQCNote(qaqc: SpacerQAQC): void {
    if (!qaqc.testInputMasterId || !this.spacerId) {
      console.warn('No testInputMasterId or spacerId available for QAQC test');
      return;
    }

    const dialogData: SpacerTestAppendNoteDialogData = {
      slurryId: this.spacerId,
      testInputMasterId: qaqc.testInputMasterId,
      originalNote: qaqc.notes || '',
      testType: 'Spacer QAQC'
    };

    this.dialog.open(SpacerTestAppendNoteDialogComponent, {
      data: dialogData,
      width: '600px'
    }).afterClosed().subscribe(result => {
      if (result?.success) {
        qaqc.notes = result.updatedNotes || '';
      }
    });
  }

  removeQAQCTest(qaqc: SpacerQAQC): void {
    if (!qaqc.testInputMasterId) {
      console.error('No testInputMasterId found for QAQC test');
      return;
    }

    if (!confirm('Are you sure you want to remove this QAQC test?')) {
      return;
    }

    this.spacerService.removeSpacerTest(qaqc.testInputMasterId, TestTypes.SpacerQAQC).subscribe({
      next: (response) => {
        console.log('Test removed successfully:', response);
        // Reload test inputs to refresh the UI
        this.loadRequestedTests();
      },
      error: (error) => {
        console.error('Error removing test:', error);
        const errorMessage = error.error?.detail || error.error?.title || 'Failed to remove test. Please try again.';
        alert(errorMessage);
      }
    });
  }
  shouldShowQAQCMessage(qaqc: any): boolean {
    // Show message if there are no readings configured, regardless of slurry status
    return !qaqc?.readings || qaqc.readings.length === 0;
  }

  removeHpHtTest(hpht: SpacerHPHTRheology): void {
    if (!hpht.testInputMasterId) {
      console.error('No testInputMasterId found for HPHT test');
      return;
    }

    if (!confirm('Are you sure you want to remove this HPHT Rheology test?')) {
      return;
    }

    this.spacerService.removeSpacerTest(hpht.testInputMasterId, TestTypes.SpacerHpHtRheology).subscribe({
      next: (response) => {
        console.log('Test removed successfully:', response);
        // Reload test inputs to refresh the UI
        this.loadRequestedTests();
      },
      error: (error) => {
        console.error('Error removing test:', error);
        const errorMessage = error.error?.detail || error.error?.title || 'Failed to remove test. Please try again.';
        alert(errorMessage);
      }
    });
  }

  removeSsstTest(ssst: SpacerSSST): void {
    if (!ssst.testInputMasterId) {
      console.error('No testInputMasterId found for SSST test');
      return;
    }

    if (!confirm('Are you sure you want to remove this SSST test?')) {
      return;
    }

    this.spacerService.removeSpacerTest(ssst.testInputMasterId, TestTypes.SpacerSSST).subscribe({
      next: (response) => {
        console.log('Test removed successfully:', response);
        // Reload test inputs to refresh the UI
        this.loadRequestedTests();
      },
      error: (error) => {
        console.error('Error removing test:', error);
        const errorMessage = error.error?.detail || error.error?.title || 'Failed to remove test. Please try again.';
        alert(errorMessage);
      }
    });
  }

  removeCompatibilityTest(compat: SpacerCompatibility): void {
    if (!compat.testInputMasterId) {
      console.error('No testInputMasterId found for Compatibility test');
      return;
    }

    if (!confirm('Are you sure you want to remove this Compatibility test?')) {
      return;
    }

    this.spacerService.removeSpacerTest(compat.testInputMasterId, TestTypes.SpacerCompatibility).subscribe({
      next: (response) => {
        console.log('Test removed successfully:', response);
        // Reload test inputs to refresh the UI
        this.loadRequestedTests();
      },
      error: (error) => {
        console.error('Error removing test:', error);
        const errorMessage = error.error?.detail || error.error?.title || 'Failed to remove test. Please try again.';
        alert(errorMessage);
      }
    });
  }

  async reviewHPHT(batch: any, hpht?: SpacerHPHTRheology): Promise<void> {

    const { ReviewHphtRheologyTest } = await import('./review-hpht-rheology-test/./review-hpht-rheology-test');

    const dialogData = {
      ...batch,
      slurryId: batch?.slurryId ?? this.spacerId,
      testInputMasterId: batch?.testInputMasterId ?? hpht?.testInputMasterId,
      temperature: batch?.temperature ?? batch?.bhct ?? hpht?.temp ?? null,
      pressure: batch?.pressure ?? hpht?.pressure ?? null,
      readings: batch?.readings ?? hpht?.readings ?? []
    };

    const dialogRef = this.dialog.open(ReviewHphtRheologyTest, {
      width: '1400px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: dialogData,
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.updated || result?.statusChanged) {
        this.loadRequestedTests();
      }
    });
  }

  removeSpacerHPHT(index: number): void {
    this.spacerHPHTList.splice(index, 1);
  }

  async recordHPHTOutput(hpht: SpacerHPHTRheology): Promise<void> {
    if (!hpht.testInputMasterId || !this.spacerId) {
      console.warn('Missing testInputMasterId or spacerId');
      return;
    }

    const { RecordOutputDialogComponent } = await import('./record-output-dialog/record-output-dialog.component');

    const dialogData = {
      slurryId: this.spacerId,
      testInputMasterId: hpht.testInputMasterId,
      testType: 'Spacer HPHT Rheology',
      testTypeId: TestTypes.SpacerHpHtRheology,
      fallbackTemperature: hpht.temp ?? null,
      fallbackPressure: hpht.pressure ?? null
    };

    const dialogRef = this.dialog.open(RecordOutputDialogComponent, {
      width: '1400px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: dialogData,
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'record' && result.batch) {
        // Open the review dialog for recording
        // TODO: Implement reviewHPHT method
        this.reviewHPHT(result.batch, hpht);
        console.log('Record HPHT for batch:', result.batch);
      } else if (result?.statusChanged || result?.updated) {
        this.loadRequestedTests();
      } else if (result?.action) {
        // Handle fail/cancel/revert actions
        console.log(`Test ${result.action}`);
        this.loadRequestedTests();
      }
    });
  }

  viewHPHTAttachments(hpht: SpacerHPHTRheology): void {
    const dialogData: SpacerTestAttachmentsDialogData = {
      testInputMasterId: hpht.testInputMasterId ?? 0,
      testType: 'Spacer HPHT Rheology'
    };

    this.dialog.open(SpacerTestAttachmentsDialogComponent, {
      data: dialogData,
      width: '1200px',
      maxWidth: '95vw'
    });
  }

  appendClearHPHTNote(hpht: SpacerHPHTRheology): void {
    if (!hpht.testInputMasterId || !this.spacerId) {
      console.warn('No testInputMasterId or spacerId available for HPHT test');
      return;
    }

    const dialogData: SpacerTestAppendNoteDialogData = {
      slurryId: this.spacerId,
      testInputMasterId: hpht.testInputMasterId,
      originalNote: hpht.notes || '',
      testType: 'Spacer HPHT Rheology'
    };

    this.dialog.open(SpacerTestAppendNoteDialogComponent, {
      data: dialogData,
      width: '600px'
    }).afterClosed().subscribe(result => {
      if (result?.success) {
        hpht.notes = result.updatedNotes || '';
      }
    });
  }

  recordHPHT(hpht: SpacerHPHTRheology): void {
    // TODO: Implement record
    console.log('Record:', hpht);
  }

  // Spacer Compatibility methods
  async recordCompatibilityOutput(compat: SpacerCompatibility): Promise<void> {
    if (!compat.testInputMasterId || !this.spacerId) {
      console.warn('Missing testInputMasterId or spacerId');
      return;
    }

    const { RecordOutputDialogComponent } = await import('./record-output-dialog/record-output-dialog.component');

    const dialogData = {
      slurryId: this.spacerId,
      testInputMasterId: compat.testInputMasterId,
      testType: 'Spacer Compatibility',
      testTypeId: TestTypes.SpacerCompatibility,
      fallbackTemperature: compat.temperature ?? null
    };

    const dialogRef = this.dialog.open(RecordOutputDialogComponent, {
      width: '1400px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: dialogData,
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('Dialog closed result:', result);

      if (result?.action === 'record' && result.batch) {
        this.reviewCompatibility(result.batch, compat);
      } else if (result?.statusChanged || result?.updated) {
        this.loadRequestedTests();
      }
    });
  }

  async reviewCompatibility(batch: any, compat?: SpacerCompatibility): Promise<void> {
    const { ReviewCompatibilityComponent } = await import('./review-compatibility/review-compatibility.component');

    const dialogData = {
      ...batch,
      temperature: batch?.temperature ?? compat?.temperature ?? null
    };

    const dialogRef = this.dialog.open(ReviewCompatibilityComponent, {
      width: '1400px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: dialogData,
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.updated || result?.statusChanged) {
        this.loadRequestedTests();
      }
    });
  }

  viewCompatibilityAttachments(compat: SpacerCompatibility): void {
    const dialogData: SpacerTestAttachmentsDialogData = {
      testInputMasterId: compat.testInputMasterId ?? 0,
      testType: 'Spacer Compatibility'
    };

    this.dialog.open(SpacerTestAttachmentsDialogComponent, {
      data: dialogData,
      width: '1200px',
      maxWidth: '95vw'
    });
  }

  appendClearCompatibilityNote(compat: SpacerCompatibility): void {
    if (!compat.testInputMasterId || !this.spacerId) {
      console.warn('No testInputMasterId or spacerId available for Compatibility test');
      return;
    }

    const dialogData: SpacerTestAppendNoteDialogData = {
      slurryId: this.spacerId,
      testInputMasterId: compat.testInputMasterId,
      originalNote: compat.notes || '',
      testType: 'Spacer Compatibility'
    };

    this.dialog.open(SpacerTestAppendNoteDialogComponent, {
      data: dialogData,
      width: '600px'
    }).afterClosed().subscribe(result => {
      if (result?.success) {
        compat.notes = result.updatedNotes || '';
      }
    });
  }

  // Spacer SSST methods
  async recordSsstOutput(ssst: SpacerSSST): Promise<void> {
    if (!ssst.testInputMasterId || !this.spacerId) {
      console.warn('Missing testInputMasterId or spacerId');
      return;
    }

    const { RecordOutputDialogComponent } = await import('./record-output-dialog/record-output-dialog.component');

    const dialogData = {
      slurryId: this.spacerId,
      testInputMasterId: ssst.testInputMasterId,
      testType: 'Spacer SSST',
      testTypeId: TestTypes.SpacerSSST,
      fallbackTemperature: ssst.temperature ?? null
    };

    const dialogRef = this.dialog.open(RecordOutputDialogComponent, {
      width: '1400px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: dialogData,
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('=== Record Dialog Closed (SSST) ===');
      console.log('Result:', result);

      if (result?.action === 'record' && result.batch) {
        console.log('Opening SSST Review Dialog with batch:', result.batch);
        // Open the review dialog for recording
        this.reviewSSST(result.batch);
      } else if (result?.statusChanged || result?.updated) {
        this.loadRequestedTests();
      } else if (result?.action) {
        // Handle fail/cancel/revert actions
        console.log(`Test ${result.action}`);
        this.loadRequestedTests();
      }
    });
  }

  async reviewSSST(batch: any): Promise<void> {
    const { ReviewSsstComponent } = await import('./review-ssst/review-ssst.component');

    const dialogRef = this.dialog.open(ReviewSsstComponent, {
      width: '1000px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: batch,
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.updated || result?.statusChanged) {
        this.loadRequestedTests();
      }
    });
  }

  viewSsstAttachments(ssst: SpacerSSST): void {
    const dialogData: SpacerTestAttachmentsDialogData = {
      testInputMasterId: ssst.testInputMasterId ?? 0,
      testType: 'Spacer SSST'
    };

    this.dialog.open(SpacerTestAttachmentsDialogComponent, {
      data: dialogData,
      width: '1200px',
      maxWidth: '95vw'
    });
  }

  appendClearSsstNote(ssst: SpacerSSST): void {
    if (!ssst.testInputMasterId || !this.spacerId) {
      console.warn('No testInputMasterId or spacerId available for SSST test');
      return;
    }

    const dialogData: SpacerTestAppendNoteDialogData = {
      slurryId: this.spacerId,
      testInputMasterId: ssst.testInputMasterId,
      originalNote: ssst.notes || '',
      testType: 'Spacer SSST'
    };

    this.dialog.open(SpacerTestAppendNoteDialogComponent, {
      data: dialogData,
      width: '600px'
    }).afterClosed().subscribe(result => {
      if (result?.success) {
        ssst.notes = result.updatedNotes || '';
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Helper Methods for Status-Based Button Display
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Check if a status is "In Progress" (status = 1)
   */
  isInProgress(status?: number): boolean {
    return status === 1; // TestOutputStatuses.InProgress
  }

  /**
   * Determine if we should show the "Record" button for a test
   * Show Record button if there are NO outputs OR if there's at least one output with InProgress status
   */
  shouldShowRecordButton(outputs?: BatchTestsBriefDto[]): boolean {
    if (!outputs || outputs.length === 0) {
      return true; // No outputs yet, show Record button
    }
    // Show Record button if any output has InProgress status
    return outputs.some(output => this.isInProgress(output.status));
  }


  /**
   * Determine if we should show the "Review" button for a test
   * Show Review button if there are outputs with status other than InProgress
   */
  shouldShowReviewButton(outputs?: BatchTestsBriefDto[]): boolean {
    if (!outputs || outputs.length === 0) {
      return false; // No outputs, no Review button
    }
    // Show Review button if any output has a status other than InProgress
    return outputs.some(output => !this.isInProgress(output.status));
  }

  /**
   * Get the display name for a test output status
   */
  getStatusDisplayName(status?: number): string {
    switch (status) {
      case 1:
        return 'In Progress';
      case 2:
        return 'Approved';
      case 3:
        return 'Rejected';
      case 4:
        return 'Repeat';
      case 5:
        return 'Failed';
      case 6:
        return 'Completed';
      case 7:
        return 'Cancelled';
      default:
        return '';
    }
  }
  /**
   * Get CSS class for status badge
   */
  getStatusClass(status?: number): string {
    switch (status) {
      case 1:
        return 'status-in-progress';
      case 2:
        return 'status-approved';
      case 3:
        return 'status-rejected';
      case 4:
        return 'status-repeat';
      case 5:
        return 'status-failed';
      case 6:
        return 'status-completed';
      case 7:
        return 'status-cancelled';
      default:
        return 'status-unknown';
    }
  }
  private readonly reviewedStatuses = [2, 3, 4];

  showReviewedBy(status?: number): boolean {
    return this.reviewedStatuses.includes(status ?? 0);
  }
  // ═══════════════════════════════════════════════════════════════
  // Button Visibility Methods (Security Conditions)
  // ═══════════════════════════════════════════════════════════════

  /**
   * Show "Remove Test" button when:
   * - Slurry status is "Ongoing in Lab" (3)
   * - AND Project status is NOT "Completed" (5)
   */
  canRemoveTest(outputs?: BatchTestsBriefDto[]): boolean {
    const hasNoBatches = !outputs || outputs.length === 0;

    return (
      hasNoBatches &&
      this.slurryStatus === SlurryStatuses.OngoinginLab &&
      this.projectStatus !== ProjectStatuses.Completed
    );
  }

  /**
   * Show "Append / Clear Note" button when:
   * - Slurry status is "Ongoing in Lab" (3)
   * - AND Project status is NOT "Completed" (5)
   */
  canAppendNote(): boolean {
    return (
      this.slurryStatus === SlurryStatuses.OngoinginLab &&
      this.projectStatus !== ProjectStatuses.Completed
    );
  }

  /**
   * Show "Is Muted" checkbox when:
   * - Slurry status is "Ongoing in Lab" (3) OR "Ready to Test" (2) OR "Draft" (1) OR "Draft Again" (6) OR "Sent to Lab Again" (7)
   * - AND Project status is NOT "Completed" (5)
   */
  canMuteTest(): boolean {
    const allowedStatuses = [
      SlurryStatuses.OngoinginLab,
      SlurryStatuses.ReadytoTest,
      SlurryStatuses.Draft,
      SlurryStatuses.DraftAgain,
      SlurryStatuses.SentToLabAgain
    ];
    return (
      allowedStatuses.includes(this.slurryStatus!) &&
      this.projectStatus !== ProjectStatuses.Completed
    );
  }
canShowRecordOutput(): boolean {
  return (
    this.slurryStatus === SlurryStatuses.OngoinginLab &&
    this.projectStatus !== ProjectStatuses.Completed
  );
}
  /**
   * Show "View Attachments" button - always visible (no restrictions)
   */
  canViewAttachments(): boolean {
    return true;
  }
  canEditRatios(): boolean {
    return this.slurryStatus === SlurryStatuses.Draft ||
      this.slurryStatus === SlurryStatuses.DraftAgain;
  }

  // ═══════════════════════════════════════════════════════════════
  // Serialise current UI state → SpacerTestInputDto for updateSpacer
  // ═══════════════════════════════════════════════════════════════

  private getEquipmentTypeId(equipmentType: string): number {
    switch (equipmentType) {
      case 'Fan77':     return 1;
      case 'Chann7600': return 2;
      case 'Chann7550': return 3;
      default:          return 0;
    }
  }

  /** Returns all batch test outputs from every test type, for use by the parent to check completion. */
  getAllBatchOutputs(): BatchTestsBriefDto[] {
    const outputs: BatchTestsBriefDto[] = [];
    for (const compat of this.spacerCompatibilityList) outputs.push(...(compat.outputs ?? []));
    for (const ssst of this.spacerSsstList) outputs.push(...(ssst.outputs ?? []));
    for (const qaqc of this.spacerQAQCList) outputs.push(...(qaqc.outputs ?? []));
    for (const hpht of this.spacerHPHTList) outputs.push(...(hpht.outputs ?? []));
    return outputs;
  }

  /** Build the SpacerTestInputDto payload from the current UI state. */
  buildTestInputDto(): SpacerTestInputDto {
    const compat = this.spacerCompatibilityList.length > 0 ? this.spacerCompatibilityList[0] : null;
    const ssst   = this.spacerSsstList.length   > 0 ? this.spacerSsstList[0]   : null;
    const qaqc   = this.spacerQAQCList.length   > 0 ? this.spacerQAQCList[0]   : null;

    return {
      spacerCompatibility: compat ? {
        testInputMasterId:       compat.testInputMasterId ?? 0,
        attachmentsCount:        compat.attachmentsCount  ?? 0,
        notes:                   compat.notes             || undefined,
        isMuted:                 compat.isMuted,
        outputs:                 compat.outputs           ?? [],
        temperature:             compat.temperature       ?? undefined,
        slurryId:                compat.slurry ? (Number(compat.slurry) || undefined) : undefined,
        ufSlurryId:              compat.ufSlurryId        || undefined,
        mudCisProContainer:      compat.mudContainer      || undefined,
        mudCisProContainerDeleted: false,
        readings:                compat.ratios && compat.ratios.length > 0 
          ? this.mapRatiosToCompatibilityReadings(compat.ratios)
          : undefined
      } : undefined,

      spacerQaQc: qaqc ? {
        testInputMasterId: qaqc.testInputMasterId ?? 0,
        attachmentsCount:  qaqc.attachmentsCount  ?? 0,
        notes:             qaqc.notes             || undefined,
        isMuted:           qaqc.isMuted,
        outputs:           qaqc.outputs           ?? [],
        temperature:       qaqc.temperature       ?? undefined,
        readings:          qaqc.readings && qaqc.readings.length > 0
          ? qaqc.readings.map(r => ({
            titleId: r.titleId,
            title: r.title
          }))
          : undefined
      } : undefined,

      spacerSsst: ssst ? {
        testInputMasterId:       ssst.testInputMasterId ?? 0,
        attachmentsCount:        ssst.attachmentsCount  ?? 0,
        notes:                   ssst.notes             || undefined,
        isMuted:                 ssst.isMuted,
        outputs:                 ssst.outputs           ?? [],
        temperature:             ssst.temperature       ?? undefined,
        mudCisProContainer:      ssst.mudContainer      || undefined,
        mudCisProContainerDeleted: false
      } : undefined,

      spacerHpHtRheologyInputs: this.spacerHPHTList.map(hpht => ({
        testInputMasterId:  hpht.testInputMasterId,
        attachmentsCount:   hpht.attachmentsCount ?? 0,
        hpHtRheologyTypeId: this.getEquipmentTypeId(hpht.equipmentType),
        readings:           (hpht.readings ?? []).map(r => ({
          temperature: r.temperature ?? undefined,
          pressure:    r.pressure    ?? undefined,
          time:        r.time        ?? undefined
        })),
        notes:       hpht.notes        || undefined,
        isMuted:     hpht.isMuted,
        batchNumber: hpht.batchNumber  ?? '',
        outputs:     hpht.outputs      ?? []
      }))
    };
  }

}
